/**
 * รายการใบสั่งผลิต — `GET /v1/production-orders`
 *
 * สามสถานะที่ต้องแยกจากกันเสมอ: กำลังโหลด / โหลดสำเร็จแต่ไม่มีรายการ /
 * โหลดไม่สำเร็จ ตารางว่างที่ใช้แทนทั้งสองอย่างหลังคือหน้าจอที่โกหกผู้ใช้
 *
 * **โรงงานเป็นขอบเขตสิทธิ์ ไม่ใช่ตัวกรองความสะดวก (D-004)** รายการที่เลือกได้จึงมา
 * จาก `plant_scope` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น และเมื่อไม่มีโรงงานให้เลือกเลย
 * หน้าจอจะไม่ยิงคำขอที่รู้อยู่แล้วว่าไม่สมบูรณ์
 *
 * การแบ่งหน้าใช้ `next_cursor` ตาม contract ไม่ใช่ offset — ปุ่ม "โหลดเพิ่ม"
 * จึงโผล่เฉพาะตอนที่เซิร์ฟเวอร์บอกว่ายังมีหน้าถัดไปจริง ๆ
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ProductionOrderFailureNotice } from './ProductionOrderFailureNotice';
import {
  orderDetailSearch,
  orderStatusLabel,
  orderStatusTone,
  type ProductionOrderListFilterPrefill,
} from './productionOrderView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { orDash } from '../products/productView';
import {
  fetchProductionOrders,
  type ProductionOrderFailure,
} from '../../lib/productionOrders/client';
import {
  PRODUCTION_ORDER_STATUSES,
  type ProductionOrder,
  type ProductionOrderListFilters,
} from '../../lib/productionOrders/types';

export interface ProductionOrderListViewProps {
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** โรงงานที่ `plant_scope` ของผู้ใช้พามาถึงจริง — ดู `ProductionOrderScreen` */
  readonly plants: readonly string[];
  /** ผู้ใช้ถือ marker ข้ามโรงงาน (AUDITOR) จึงเลือก "ทุกโรงงานในขอบเขต" ได้ */
  readonly crossPlant: boolean;
  /** มาจาก flag `can_create` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น */
  readonly canCreate: boolean;
  readonly createHref: string;
  /**
   * ตัวกรองเริ่มต้นจากลิงก์ "ดูสถานะ" ของหน้าแผนการผลิต (D-127 ข้อ 4) — ใช้เป็น
   * ค่าเริ่มต้นของ state เท่านั้น แก้ต่อได้เหมือนตัวกรองปกติ
   */
  readonly filterPrefill?: ProductionOrderListFilterPrefill;
}

type ListState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly items: readonly ProductionOrder[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
      /** ความล้มเหลวของ "โหลดเพิ่ม" ไม่ควรลบแถวที่โหลดมาแล้วทิ้ง */
      readonly loadMoreFailure: ProductionOrderFailure | null;
    }
  | ProductionOrderFailure;

/** ค่าใน `<select>` ที่แปลว่า "ไม่ส่ง `plant_code` ให้เซิร์ฟเวอร์กรองตาม scope เอง" */
const ALL_PLANTS_VALUE = '';

export function ProductionOrderListView({
  apiBaseUrl,
  fetchImpl,
  plants,
  crossPlant,
  canCreate,
  createHref,
  filterPrefill,
}: ProductionOrderListViewProps) {
  const { pathname } = useLocation();

  /**
   * โรงงานเริ่มต้น: โรงงานแรกที่สิทธิ์พามาถึง — ไม่ใช่ค่าที่หน้าจอคิดเอง
   *
   * ผู้ที่ถือ marker ข้ามโรงงานอย่างเดียว (AUDITOR) ไม่มีรหัสโรงงานให้เลือก
   * จึงเริ่มที่ "ทุกโรงงานในขอบเขตของคุณ" ซึ่งแปลว่าไม่ส่ง `plant_code` ไป
   *
   * `filterPrefill` (D-127 ข้อ 4) เลื่อนค่าเริ่มต้นนี้ได้ครั้งเดียวตอน mount —
   * `plant_code` ที่ไม่อยู่ในสิทธิ์ถูกเมินไปแล้วที่ `resolveProductionOrderListFilterPrefill`
   */
  const defaultPlantCode = plants[0] ?? ALL_PLANTS_VALUE;
  const [plantCode, setPlantCode] = useState<string>(
    filterPrefill?.plantCode || defaultPlantCode,
  );
  const [statusDraft, setStatusDraft] = useState('');
  const [productDraft, setProductDraft] = useState(filterPrefill?.productCode ?? '');
  const [filters, setFilters] = useState<ProductionOrderListFilters>({
    product_code: filterPrefill?.productCode ?? '',
  });
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<ListState>({ status: 'loading' });

  // ปุ่ม "ล้างตัวกรอง" (แบบเดียวกับหน้ารายการใบสั่งขาย) โผล่เฉพาะตอนมีตัวกรอง
  // ที่ใช้งานอยู่จริง ไม่ใช่แค่ร่างที่พิมพ์ค้างไว้ในช่อง
  const hasActiveFilters =
    (filters.status ?? '') !== '' ||
    (filters.product_code ?? '') !== '' ||
    plantCode !== defaultPlantCode;

  const resetFilters = () => {
    setStatusDraft('');
    setProductDraft('');
    setFilters({});
    setPlantCode(defaultPlantCode);
  };

  // ไม่มีโรงงานให้เลือกเลย = คำขอที่รู้อยู่แล้วว่าไม่สมบูรณ์ จึงไม่ยิง
  const canQuery = plants.length > 0 || crossPlant;

  useEffect(() => {
    if (!canQuery) {
      return;
    }
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchProductionOrders(
      { ...filters, ...(plantCode === ALL_PLANTS_VALUE ? {} : { plant_code: plantCode }) },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(
        result.status === 'ready'
          ? {
              status: 'ready',
              items: result.page.items,
              nextCursor: result.page.next_cursor,
              loadingMore: false,
              loadMoreFailure: null,
            }
          : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [canQuery, plantCode, filters, apiBaseUrl, fetchImpl, reloadToken]);

  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFilters({ status: statusDraft, product_code: productDraft });
  };

  const loadMore = async () => {
    if (state.status !== 'ready' || state.loadingMore) {
      return;
    }
    const loaded = state;
    const cursor = loaded.nextCursor;
    if (cursor === null) {
      return;
    }
    setState({ ...loaded, loadingMore: true, loadMoreFailure: null });

    const result = await fetchProductionOrders(
      {
        ...filters,
        ...(plantCode === ALL_PLANTS_VALUE ? {} : { plant_code: plantCode }),
        cursor,
      },
      { apiBaseUrl, fetchImpl },
    );
    if (result.status === 'cancelled') {
      return;
    }
    setState(
      result.status === 'ready'
        ? {
            status: 'ready',
            items: [...loaded.items, ...result.page.items],
            nextCursor: result.page.next_cursor,
            loadingMore: false,
            loadMoreFailure: null,
          }
        : { ...loaded, loadingMore: false, loadMoreFailure: result },
    );
  };

  return (
    <div className="cap-page" data-testid="production-order-list">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ใบสั่งผลิต{' '}
            <InfoHint
              label="เกี่ยวกับขอบเขตโรงงานที่แสดงในรายการนี้"
              text="รายการถูกจำกัดด้วยโรงงานที่คุณมีสิทธิ์เห็นเสมอ (D-004) — ใบสั่งผลิตของโรงงานอื่นจะไม่ปรากฏที่นี่แม้จะมีอยู่จริง"
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          {canCreate ? (
            <Link
              className="cap-button cap-button--primary"
              data-testid="production-order-create-link"
              to={{ pathname, search: createHref }}
            >
              เปิดใบสั่งผลิตใหม่
            </Link>
          ) : null}
        </div>
      </div>

      {!canQuery ? (
        <div className="cap-notice" data-testid="production-order-no-plant" role="status">
          <StatusBadge tone="warning">ไม่มีโรงงานที่คุณเห็น</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ของคุณยังไม่ครอบคลุมโรงงานใดเลย</h2>
          <p className="cap-notice__text">
            ใบสั่งผลิตเป็นของโรงงานใดโรงงานหนึ่งเสมอ และ <code className="cap-table__code">plant_code</code>{' '}
            เป็นขอบเขตสิทธิ์ ไม่ใช่ตัวกรอง (D-004) — เมื่อยังไม่มีโรงงานให้เลือก หน้าจอนี้จึงไม่ยิงคำขอที่
            รู้อยู่แล้วว่าไม่สมบูรณ์ ติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์โรงงาน
          </p>
        </div>
      ) : null}

      {canQuery ? (
        <form className="cap-filters" onSubmit={applyFilters} aria-label="ตัวกรองรายการใบสั่งผลิต">
          <label className="cap-field">
            <span className="cap-field__label">
              โรงงาน (plant_code){' '}
              <InfoHint
                label="เกี่ยวกับรหัสโรงงาน"
                text="เลือกได้เฉพาะโรงงานที่สิทธิ์ของคุณพามาถึง — เซิร์ฟเวอร์กรองตามขอบเขตของคุณซ้ำอีกชั้นเสมอ"
              />
            </span>
            <select
              className="cap-input"
              name="plant_code"
              data-testid="production-order-plant-select"
              value={plantCode}
              onChange={(event) => {
                setPlantCode(event.target.value);
              }}
            >
              {crossPlant ? (
                <option value={ALL_PLANTS_VALUE}>ทุกโรงงานในขอบเขตของคุณ</option>
              ) : null}
              {plants.map((code) => (
                <option key={code} value={code}>
                  โรงงาน {code}
                </option>
              ))}
            </select>
          </label>
          <label className="cap-field">
            <span className="cap-field__label">สถานะ</span>
            <select
              className="cap-input"
              name="status"
              data-testid="production-order-status-filter"
              value={statusDraft}
              onChange={(event) => {
                setStatusDraft(event.target.value);
              }}
            >
              <option value="">ทุกสถานะ</option>
              {PRODUCTION_ORDER_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {orderStatusLabel(status)}
                </option>
              ))}
            </select>
          </label>
          <label className="cap-field">
            <span className="cap-field__label">รหัสสินค้า</span>
            <input
              className="cap-input"
              type="search"
              name="product_code"
              data-testid="production-order-product-filter"
              value={productDraft}
              onChange={(event) => {
                setProductDraft(event.target.value);
              }}
              placeholder="เช่น RM-STEEL-8MM"
            />
          </label>
          <button type="submit" className="cap-button cap-button--ghost">
            กรองรายการ
          </button>
          {hasActiveFilters ? (
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="production-order-filters-reset"
              onClick={resetFilters}
            >
              ล้างตัวกรอง
            </button>
          ) : null}
        </form>
      ) : null}

      {canQuery && state.status === 'loading' ? (
        <div className="cap-notice" data-testid="production-order-list-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการใบสั่งผลิต…</p>
        </div>
      ) : null}

      {canQuery && state.status !== 'loading' && state.status !== 'ready' ? (
        <ProductionOrderFailureNotice
          failure={state}
          context="list"
          plantCode={plantCode}
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <div className="cap-notice" data-testid="production-order-list-empty" role="status">
          <StatusBadge tone="neutral">ไม่มีรายการ</StatusBadge>
          <h2 className="cap-notice__title">ไม่พบใบสั่งผลิตตามเงื่อนไขนี้</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่ไม่มีใบสั่งผลิตที่ตรงเงื่อนไขและอยู่ในโรงงานที่คุณมีสิทธิ์เห็น —
            ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="รายการใบสั่งผลิต">
          <div className="cap-card__head">
            <h2 className="cap-card__title">รายการใบสั่งผลิต</h2>
            <span className="cap-card__hint">แสดง {state.items.length} รายการที่โหลดมาแล้ว</span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table">
              <caption className="cap-visually-hidden">
                รายการใบสั่งผลิตที่คุณมีสิทธิ์เห็น พร้อมสถานะ จำนวนที่วางแผน และช่วงเวลาที่วางแผนไว้
              </caption>
              <thead>
                <tr>
                  <th scope="col">เลขที่ใบสั่งผลิต</th>
                  <th scope="col">สินค้า</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">จำนวนที่วางแผน</th>
                  <th scope="col">เริ่มตามแผน</th>
                  <th scope="col">สิ้นสุดตามแผน</th>
                  <th scope="col">สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((order) => (
                  <tr
                    key={order.production_order_id}
                    data-testid={`production-order-row-${order.production_order_id}`}
                    data-status={order.status}
                  >
                    <th scope="row" className="cap-table__row-head">
                      <Link
                        className="cap-table__link"
                        to={{
                          pathname,
                          search: orderDetailSearch(order.production_order_id),
                        }}
                      >
                        {orDash(order.order_no)}
                      </Link>
                    </th>
                    <td className="cap-table__code">{orDash(order.product_code)}</td>
                    <td className="cap-table__code">{orDash(order.plant_code)}</td>
                    {/* NUMERIC เดินทางเป็น string ตลอด — ไม่จัดรูปแบบซ้ำที่นี่ */}
                    <td className="cap-table__code">{orDash(order.planned_quantity)}</td>
                    <td className="cap-table__code">{orDash(order.planned_start_date)}</td>
                    <td className="cap-table__code">{orDash(order.planned_end_date)}</td>
                    <td>
                      <StatusBadge tone={orderStatusTone(order.status)}>
                        {orderStatusLabel(order.status)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cap-card__foot">
            {state.nextCursor === null ? (
              <span data-testid="production-order-list-complete">
                แสดงครบทุกรายการที่ตรงเงื่อนไขแล้ว
              </span>
            ) : (
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="production-order-list-more"
                onClick={() => {
                  void loadMore();
                }}
                disabled={state.loadingMore}
              >
                {state.loadingMore ? 'กำลังโหลดเพิ่ม…' : 'โหลดรายการถัดไป'}
              </button>
            )}
          </div>
        </section>
      ) : null}

      {state.status === 'ready' && state.loadMoreFailure !== null ? (
        <ProductionOrderFailureNotice
          failure={state.loadMoreFailure}
          context="list"
          plantCode={plantCode}
        />
      ) : null}
    </div>
  );
}
